/* eslint-disable complexity */
import React from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '@futrli/localisation';
import { ContactDetails } from '@futrli-sdk/predict';

import * as styles from './payment-trends.scss';
import { CUSTOMER_SUPPLIER_KIND } from '../const';

const MAX_DAYS_NUM = 75;

export const PaymentsTrends = ({ activeData }: { activeData?: ContactDetails }) => {
  if (!activeData) {
    return null;
  }

  const {
    average_days_to_pay_last_3_invoices,
    average_days_to_pay_last_10_invoices,
    average_days_to_pay_this_year,
    average_days_to_pay_last_year
  } = activeData;

  const avgDaysLast3 =
    average_days_to_pay_last_3_invoices > MAX_DAYS_NUM ? MAX_DAYS_NUM : average_days_to_pay_last_3_invoices;
  const avgDaysLast10 =
    average_days_to_pay_last_10_invoices > MAX_DAYS_NUM ? MAX_DAYS_NUM : average_days_to_pay_last_10_invoices;
  const avgDaysThisYear = average_days_to_pay_this_year > MAX_DAYS_NUM ? MAX_DAYS_NUM : average_days_to_pay_this_year;
  const avgDaysLastYear = average_days_to_pay_last_year > MAX_DAYS_NUM ? MAX_DAYS_NUM : average_days_to_pay_last_year;

  return (
    <div style={{ marginTop: 32 }}>
      <div className={classNames(styles.labelFont, styles.labelGrey)} style={{ marginBottom: 8 }}>
        <FormattedMessage id="predict.payment_trends" defaultMessage="Payment trends" />
      </div>

      {/* Last 3 invoices */}
      <div className={styles.paymentTrendsContainer}>
        <div
          className={classNames(styles.paymentTrendsContent, {
            [styles.paymentTrendsCustomerFill]: activeData.type === CUSTOMER_SUPPLIER_KIND.CUSTOMER,
            [styles.paymentTrendsSupplierFill]: activeData.type === CUSTOMER_SUPPLIER_KIND.SUPPLIER
          })}
          style={{ width: `calc(${avgDaysLast3} / 75 * 100%)` }}
        ></div>
      </div>
      <div className={styles.axisLabelContainer}>
        <div className={classNames(styles.labelFont, styles.labelDark)}>
          {activeData.type === CUSTOMER_SUPPLIER_KIND.CUSTOMER ? (
            <FormattedMessage id="predict.last_3_invoices" defaultMessage="Last 3 invoices" />
          ) : (
            <FormattedMessage id="predict.performance.last_3_bills" defaultMessage="Last 3 bills" />
          )}
        </div>
        <div className={classNames(styles.labelFont, styles.labelDark)}>
          <FormattedMessage
            id="predict.days_num"
            defaultMessage="{daysNum, number} {daysNum, plural, one {day} other {days}}"
            values={{ daysNum: average_days_to_pay_last_3_invoices }}
          />
        </div>
      </div>

      {/* Last 10 invoices */}
      <div className={styles.paymentTrendsContainer}>
        <div
          className={classNames(styles.paymentTrendsContent, {
            [styles.paymentTrendsCustomerFill]: activeData.type === CUSTOMER_SUPPLIER_KIND.CUSTOMER,
            [styles.paymentTrendsSupplierFill]: activeData.type === CUSTOMER_SUPPLIER_KIND.SUPPLIER
          })}
          style={{
            width: `calc(${avgDaysLast10} / 75 * 100%)`
          }}
        ></div>
      </div>
      <div className={styles.axisLabelContainer}>
        <div className={classNames(styles.labelFont, styles.labelDark)}>
          {activeData.type === CUSTOMER_SUPPLIER_KIND.CUSTOMER ? (
            <FormattedMessage id="predict.performance.last_10_invoices" defaultMessage="Last 10 invoices" />
          ) : (
            <FormattedMessage id="predict.performance.last_10_bills" defaultMessage="Last 10 bills" />
          )}
        </div>
        <div className={classNames(styles.labelFont, styles.labelDark)}>
          <FormattedMessage
            id="predict.days_num"
            defaultMessage="{daysNum, number} {daysNum, plural, one {day} other {days}}"
            values={{ daysNum: average_days_to_pay_last_10_invoices }}
          />
        </div>
      </div>

      {/* This year */}
      <div className={styles.paymentTrendsContainer}>
        <div
          className={classNames(styles.paymentTrendsContent, {
            [styles.paymentTrendsCustomerFill]: activeData.type === CUSTOMER_SUPPLIER_KIND.CUSTOMER,
            [styles.paymentTrendsSupplierFill]: activeData.type === CUSTOMER_SUPPLIER_KIND.SUPPLIER
          })}
          style={{ width: `calc(${avgDaysThisYear} / 75 * 100%)` }}
        ></div>
      </div>
      <div className={styles.axisLabelContainer}>
        <div className={classNames(styles.labelFont, styles.labelDark)}>
          <FormattedMessage id="common.this_year" defaultMessage="This year" />
        </div>
        <div className={classNames(styles.labelFont, styles.labelDark)}>
          <FormattedMessage
            id="predict.days_num"
            defaultMessage="{daysNum, number} {daysNum, plural, one {day} other {days}}"
            values={{ daysNum: average_days_to_pay_this_year }}
          />
        </div>
      </div>

      {/* Last year */}
      <div className={styles.paymentTrendsContainer}>
        <div
          className={classNames(styles.paymentTrendsContent, {
            [styles.paymentTrendsCustomerFill]: activeData.type === CUSTOMER_SUPPLIER_KIND.CUSTOMER,
            [styles.paymentTrendsSupplierFill]: activeData.type === CUSTOMER_SUPPLIER_KIND.SUPPLIER
          })}
          style={{ width: `calc(${avgDaysLastYear} / 75 * 100%)` }}
        ></div>
      </div>
      <div className={styles.axisLabelContainer}>
        <div className={classNames(styles.labelFont, styles.labelDark)}>
          <FormattedMessage id="common.last_year" defaultMessage="Last year" />
        </div>
        <div className={classNames(styles.labelFont, styles.labelDark)}>
          <FormattedMessage
            id="predict.days_num"
            defaultMessage="{daysNum, number} {daysNum, plural, one {day} other {days}}"
            values={{ daysNum: average_days_to_pay_last_year }}
          />
        </div>
      </div>

      <div className={styles.axis} />
      <div className={styles.axisValues}>
        <div className={classNames(styles.labelFont, styles.labelDark)}>{'0'}</div>
        <div className={classNames(styles.labelFont, styles.labelDark)}>{'15'}</div>
        <div className={classNames(styles.labelFont, styles.labelDark)}>{'30'}</div>
        <div className={classNames(styles.labelFont, styles.labelDark)}>{'45'}</div>
        <div className={classNames(styles.labelFont, styles.labelDark)}>{'60'}</div>
        <div className={classNames(styles.labelFont, styles.labelDark)}>{'75+'}</div>
      </div>
    </div>
  );
};
